import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check, X as XIcon, MapPin, Building2, Users, Sparkles, Camera, ScrollText, FolderCheck, Globe2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import BeforeAfter from "@/components/site/BeforeAfter";
import PricingCard from "@/components/site/PricingCard";
import Reveal from "@/components/site/Reveal";
import SEO from "@/components/site/SEO";
import AudiencePaths from "@/components/site/AudiencePaths";
import ServicesGrid from "@/components/site/ServicesGrid";
import IntakeSelector from "@/components/site/IntakeSelector";
import { LOGO, IMG } from "@/data/media";
import { singleListingPlans, recurringPlans, addOns } from "@/data/pricing";
import { faqItems } from "@/data/faq";
import { priorityCities, regionalCoverage } from "@/data/serviceArea";
import { localBusinessSchema, serviceSchema, organizationSchema, DEFAULT_DESCRIPTION } from "@/data/site";

const listingBreakdown = [
  "Rushed photos",
  "Missing property details",
  "Uneven room documentation",
  "Weak feature capture",
  "Delayed listing materials",
  "Inconsistent website listing pages",
  "Scattered responsibility",
  "Seller follow-up pressure",
  "Agent time lost to coordination",
  "Nobody truly owning the standard",
];

const deliverables = [
  { icon: Camera,      label: "Professional property photography" },
  { icon: Building2,   label: "Exterior, interior, room and feature capture" },
  { icon: ScrollText,  label: "Key property detail notes" },
  { icon: ScrollText,  label: "Room and feature documentation" },
  { icon: Check,       label: "MLS-ready photo exports" },
  { icon: Globe2,      label: "Web-ready photo exports" },
  { icon: FolderCheck, label: "Organized folder delivery" },
  { icon: Check,       label: "Listing-readiness checklist" },
  { icon: Sparkles,    label: "Property highlight notes for agent review" },
  { icon: Globe2,      label: "Support materials for agent / brokerage websites" },
  { icon: Users,       label: "Social-ready image selections (where included)" },
  { icon: ScrollText,  label: "Print-friendly photo selections (where included)" },
];

const benefits = [
  { title: "Seller Confidence", body: "A properly prepared listing reassures sellers that their property is being handled with care." },
  { title: "Buyer Trust",       body: "Clean photos and complete details make the property easier to understand and easier to take seriously." },
  { title: "Agent Reputation",  body: "Every listing becomes evidence of the agent's standard." },
  { title: "Marketing Performance", body: "The rest of the marketing works better when the listing foundation is right." },
];

const diyBurdenItems = [
  "Agent time",
  "Admin time",
  "Vendor coordination",
  "Missed details",
  "Revision loops",
  "Seller follow-up",
  "Website delays",
  "Inconsistent output",
  "Hidden management burden",
];

const lotwiseItems = [
  "Structured property visit",
  "Photo capture",
  "Feature documentation",
  "Listing-ready assets",
  "Organized delivery",
  "Owned-channel support",
  "Repeatable standard",
  "Clear accountability",
];

const processSteps = [
  { t: "Review the listing",              b: "We confirm the property, location, timeline, support level, and any seller or agent priorities before the visit." },
  { t: "Visit the property",              b: "LotWise Home captures the property with a listing-first mindset, focusing on rooms, flow, features, curb appeal, and buyer-facing presentation." },
  { t: "Capture photos and property details", b: "Photos are captured alongside key listing information, room notes, feature highlights, upgrades, and presentation details." },
  { t: "Prepare listing-ready assets",    b: "The final materials are organized so the agent, team, or brokerage can use them for MLS, agent websites, brokerage pages, print materials, and social promotion." },
  { t: "Support owned-channel presentation", b: "LotWise Home provides the materials needed to keep agent and brokerage website listings stronger, cleaner, and more consistent." },
  { t: "Maintain the standard",           b: "For monthly and team plans, LotWise Home creates a repeatable listing rhythm so the standard does not have to be rebuilt every time." },
];

const proofCards = [
  { t: "Ownership",       b: "The listing has a defined execution layer." },
  { t: "Consistency",     b: "Each property follows a stronger capture and presentation standard." },
  { t: "Control",         b: "The agent knows what is being captured, prepared, and delivered." },
  { t: "Professionalism", b: "The seller and buyer see a listing that feels properly handled." },
];

export default function Home() {
  const allPlans = [...singleListingPlans, ...recurringPlans];
  const homeFaqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.slice(0, 10).map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <div className="w-full">
      <SEO
        title={null}
        description={DEFAULT_DESCRIPTION}
        path="/"
        schema={[localBusinessSchema, serviceSchema, organizationSchema, homeFaqSchema]}
      />
      {/* ============================ HERO ============================ */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-brand-black text-white"
      >
        <div className="absolute inset-0 dot-grid-dark opacity-30 pointer-events-none" aria-hidden />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden
          style={{
            background:
              "radial-gradient(60% 60% at 80% 10%, rgba(79,13,255,0.25) 0%, rgba(0,0,0,0) 60%), radial-gradient(50% 50% at 5% 90%, rgba(10,181,255,0.18) 0%, rgba(0,0,0,0) 65%)",
          }}
        />
        <div className="relative mx-auto max-w-[1360px] px-5 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <div className="inline-flex items-center gap-2.5 border border-white/15 rounded-full px-3 py-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-blue" />
                <span className="text-[11px] tracking-[0.22em] uppercase font-medium text-white/80">
                  Managed listing services · Southeastern Ontario
                </span>
              </div>

              <h1 className="mt-7 text-[44px] sm:text-6xl lg:text-[76px] font-bold tracking-[-0.02em] leading-[0.98]">
                One managed process to get a property <span className="text-brand-gradient">market-ready.</span>
              </h1>

              <p className="mt-7 max-w-[640px] text-[16px] md:text-[18px] leading-relaxed text-white/70">
                LotWise Home coordinates the photography, video, aerial, media, listing preparation,
                marketing assets, and vendor communication required to prepare, present, and
                maintain a property through the listing process. Built for real estate agents, teams,
                and brokerages, with dedicated paths for builders, developers, investors, and
                property owners.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center gap-2 bg-white text-brand-black px-6 py-4 text-[14px] font-bold tracking-tight hover:bg-brand-blue hover:text-white transition-colors"
                >
                  Get My Property Market-Ready
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <a
                  href="#what-lotwise-handles"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-4 text-[14px] font-bold tracking-tight hover:border-white transition-colors"
                >
                  See What's Included
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-[13px] text-white/60">
                <div><span className="text-white font-semibold">Listing support</span> from $395</div>
                <div className="hidden sm:block h-4 w-px bg-white/20 self-center" />
                <div><span className="text-white font-semibold">Monthly support</span> from $1,295</div>
                <div className="hidden sm:block h-4 w-px bg-white/20 self-center" />
                <div className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Southeastern Ontario · Waitlist for other regions</div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <Reveal className="relative">
                <div className="absolute -inset-8 bg-gradient-to-br from-brand-blue/20 to-brand-violet/20 blur-3xl -z-10" aria-hidden />
                <div className="relative border border-white/15 overflow-hidden">
                  <img
                    src={IMG.hero}
                    alt="Editorial listing photograph of a modern living room at golden hour"
                    className="w-full h-[380px] md:h-[520px] object-cover"
                    onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1628744876497-eb30460be9f6?w=1400&q=85"; }}
                  />
                  <div className="absolute top-4 left-4 flex flex-col gap-2">
                    <span className="inline-block px-2.5 py-1 bg-white text-black text-[10px] tracking-[0.24em] uppercase font-semibold">
                      Listing Standard
                    </span>
                    <span className="inline-block px-2.5 py-1 bg-black/70 text-white text-[10px] tracking-[0.24em] uppercase font-medium">
                      Web-ready · MLS-ready
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                    <div className="text-[11px] text-white/70 tracking-[0.2em] uppercase">Capture · Document · Deliver</div>
                    <div className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-blue" />
                      <span className="text-[11px] text-white/70 tracking-widest">READY</span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  {[["24", "photo set"], ["12", "room notes"], ["06", "highlights"]].map(([n, l]) => (
                    <div key={l} className="border border-white/12 px-3 py-3">
                      <div className="text-2xl font-bold text-brand-blue num-tick">{n}</div>
                      <div className="text-[11px] tracking-[0.16em] uppercase text-white/60 mt-1">{l}</div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== RECOGNITION ==================== */}
      <section className="bg-white text-brand-black">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow-accent">01 · The problem</div>
              <h2 className="mt-4 text-[34px] md:text-5xl font-bold tracking-tight leading-[1.06]">
                Getting a property market-ready is not one job. It is many jobs, spread across many people.
              </h2>
            </div>
            <Reveal className="lg:col-span-7 space-y-5 text-[16px] md:text-[17px] leading-relaxed text-brand-gray">
              <p>A single listing usually needs photography, video, aerial capture, property information, feature notes, visual presentation, marketing assets, vendor communication, listing preparation, website updates, and ongoing changes. Those tasks are almost always split across several people and providers.</p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-brand-black">
                {["Photography is booked separately.", "Room and feature notes live in someone's inbox.", "Website updates lag behind.", "Video, drone, and AI walkthroughs run on their own timelines.", "Print and social pieces are handled last.", "Nobody has a full view of the property's presentation."].map((s) => (
                  <li key={s} className="flex items-start gap-2.5 text-[15px]">
                    <span className="mt-2 h-1 w-4 bg-brand-black shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-brand-black text-[19px] md:text-[22px] font-medium leading-snug tracking-tight">
                The work is fragmented. LotWise Home is the one managed process around it.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Audience paths */}
      <AudiencePaths />
      <section className="relative bg-[#F7F7F5] text-brand-black">
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none dot-grid-fade" aria-hidden />
        <div className="relative mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="max-w-2xl">
            <div className="eyebrow-accent">02 · The Breakdown</div>
            <h2 className="mt-4 text-[32px] md:text-5xl font-bold tracking-tight leading-[1.08]">
              What actually slips when the standard isn't owned.
            </h2>
            <p className="mt-5 text-[16px] leading-relaxed text-brand-gray">
              When photos, details, room notes, website updates, and listing materials are handled
              wherever time can be found, the standard slips.
            </p>
          </div>
          <Reveal className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {listingBreakdown.map((item, i) => (
              <div key={item} className="group bg-white border border-brand-gray/20 p-4 hover:border-brand-black transition-colors">
                <div className="text-[10px] tracking-[0.22em] uppercase text-brand-gray">{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-3 text-[14px] font-medium text-brand-black leading-snug">{item}</div>
                <div className="mt-4 flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-brand-gray">
                  <XIcon className="h-3 w-3" /> Slips
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* What LotWise Handles */}
      <ServicesGrid />

      {/* ==================== BEFORE / AFTER PROOF ==================== */}
      <section className="bg-white text-brand-black">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-6">
              <div className="eyebrow-accent">03 · Proof</div>
              <h2 className="mt-4 text-[34px] md:text-6xl font-bold tracking-tight leading-[1.02]">
                Better presentation is visible before it is explained.
              </h2>
            </div>
            <div className="lg:col-span-6 text-[16px] leading-relaxed text-brand-gray max-w-xl">
              <p>The difference is presentation, not renovation.</p>
              <p className="mt-3">A rushed listing feels rushed. A properly presented listing feels easier to trust. The change you see below is what LotWise Home does: framing, lighting, composition, and staging-of-the-photo — not renovation, and not physical staging.</p>
              <p className="mt-3 text-brand-black font-medium">What looks handled properly feels easier to trust.</p>
            </div>
          </div>

          <div className="mt-14 grid lg:grid-cols-2 gap-6">
            {IMG.pairs.slice(0, 2).map((p) => (
              <Reveal key={p.slug}>
                <BeforeAfter
                  before={`/generated/${p.slug}-before.png`}
                  after={`/generated/${p.slug}-after.png`}
                  label={p.label}
                  testId={`hero-before-after-${p.slug}`}
                />
                <div className="mt-3 flex items-center justify-between text-[12px] tracking-[0.18em] uppercase text-brand-gray">
                  <span>{p.label}</span>
                  <span>Drag to compare</span>
                </div>
                <p className="mt-3 text-[12px] leading-relaxed text-brand-gray border-l-2 border-brand-blue/60 pl-3">
                  <span className="text-brand-black font-semibold">LotWise role: </span>
                  photography, framing, composition, and presentation planning. LotWise does not perform renovations, construction, or physical staging.
                </p>
              </Reveal>
            ))}
          </div>

          <div className="mt-12">
            <Link to="/listing-standard" className="group inline-flex items-center gap-2 text-[14px] font-bold tracking-tight text-brand-black link-underline">
              See more before / after pairs
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== WHAT YOU RECEIVE ==================== */}
      <section className="relative bg-brand-black text-white">
        <div className="absolute inset-0 dot-grid-dark opacity-25 pointer-events-none" aria-hidden />
        <div className="relative mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow text-white/60">04 · Deliverables</div>
              <h2 className="mt-4 text-[34px] md:text-5xl font-bold tracking-tight leading-[1.06]">
                What you receive after a LotWise Home listing visit.
              </h2>
              <p className="mt-6 text-[16px] leading-relaxed text-white/60">
                A stronger listing starts with better capture, cleaner organization, and a clearer
                standard for what needs to be ready before the property goes live. Depending on the
                selected service level, LotWise Home prepares the materials needed to support MLS
                readiness, agent websites, brokerage pages, print materials, and social listing
                promotion.
              </p>
            </div>
            <Reveal className="lg:col-span-7">
              <div className="grid sm:grid-cols-2 gap-3">
                {deliverables.map((d, i) => {
                  const Ic = d.icon;
                  return (
                    <div key={i} className="group border border-white/12 p-4 hover:border-brand-blue/60 transition-colors">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 h-8 w-8 flex items-center justify-center rounded-sm bg-white/5 border border-white/10">
                          <Ic className="h-4 w-4 text-brand-blue" />
                        </span>
                        <div>
                          <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">Item {String(i + 1).padStart(2, "0")}</div>
                          <div className="mt-1 text-[14px] font-medium leading-snug">{d.label}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ==================== WHY IT MATTERS ==================== */}
      <section className="bg-white text-brand-black">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="eyebrow-accent">05 · Why It Matters</div>
            <h2 className="mt-4 text-[34px] md:text-5xl font-bold tracking-tight leading-[1.05]">
              The listing shapes the conversation before the showing.
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] leading-relaxed text-brand-gray">
              Complete listings do more than look better. They make the property easier to trust.
              Better traffic performs better when the listing presentation is complete, current, and
              credible. What the buyer sees online shapes the conversation before the agent ever gets
              a chance to have it.
            </p>
          </div>

          <Reveal className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {benefits.map((b, i) => (
              <div key={b.title} className="border-t-2 border-brand-black bg-white pt-6 pr-4 pb-6">
                <div className="text-[10px] tracking-[0.22em] uppercase text-brand-gray">Benefit {String(i + 1).padStart(2, "0")}</div>
                <div className="mt-3 text-[20px] md:text-[22px] font-bold tracking-tight">{b.title}</div>
                <p className="mt-3 text-[14px] leading-relaxed text-brand-gray">{b.body}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ==================== REAL COST OF INTERNAL HANDLING ==================== */}
      <section className="relative bg-[#F7F7F5] text-brand-black">
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none dot-grid-fade" aria-hidden />
        <div className="relative mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="eyebrow-accent">06 · The Real Cost</div>
            <h2 className="mt-4 text-[34px] md:text-5xl font-bold tracking-tight leading-[1.05]">
              Internal handling looks cheaper until the whole process is counted.
            </h2>
            <p className="mt-6 text-[16px] leading-relaxed text-brand-gray">
              Handling listing photos, property details, website updates, and marketing materials
              internally sounds simple until the standard has to hold across every listing. Once
              agent time, admin time, vendor coordination, revisions, seller communication, quality
              control, website updates, and rework are added up, the cost is often higher than
              expected and the output is still inconsistent.
            </p>
          </div>

          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-brand-gray/25 p-6 md:p-8">
              <div className="flex items-center justify-between">
                <div className="eyebrow text-brand-gray">Column A</div>
                <span className="text-[10px] tracking-[0.24em] uppercase text-brand-gray">DIY / Scattered</span>
              </div>
              <h3 className="mt-3 text-2xl md:text-3xl font-bold tracking-tight">Scattered Internal Handling</h3>
              <div className="mt-6 text-4xl md:text-5xl font-bold tracking-tight num-tick">
                $1,500 – $4,500<span className="text-brand-gray text-2xl md:text-3xl font-medium">+ / mo</span>
              </div>
              <div className="mt-2 text-[12px] tracking-[0.14em] uppercase text-brand-gray">Estimated real operating burden</div>

              <ul className="mt-8 divide-y divide-brand-gray/20">
                {diyBurdenItems.map((i) => (
                  <li key={i} className="flex items-center justify-between py-3">
                    <span className="text-[14px] text-brand-black/70 line-through decoration-brand-gray/40">{i}</span>
                    <XIcon className="h-4 w-4 text-brand-gray" />
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-brand-black text-white p-6 md:p-8 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-brand-violet/30 blur-3xl" aria-hidden />
              <div className="flex items-center justify-between relative">
                <div className="eyebrow text-white/50">Column B</div>
                <span className="text-[10px] tracking-[0.24em] uppercase text-brand-blue">LotWise Home</span>
              </div>
              <h3 className="mt-3 text-2xl md:text-3xl font-bold tracking-tight relative">A controlled execution layer</h3>
              <div className="mt-6 text-4xl md:text-5xl font-bold tracking-tight relative">
                <span className="text-brand-gradient">$395 – $2,795</span>
                <span className="text-white/50 text-2xl md:text-3xl font-medium"> / listing or mo</span>
              </div>
              <div className="mt-2 text-[12px] tracking-[0.14em] uppercase text-white/50">Handled as a system, on schedule</div>

              <ul className="mt-8 divide-y divide-white/10 relative">
                {lotwiseItems.map((i) => (
                  <li key={i} className="flex items-center justify-between py-3">
                    <span className="text-[14px] font-medium">{i}</span>
                    <span className="h-5 w-5 rounded-full bg-gradient-to-br from-brand-blue to-brand-violet flex items-center justify-center">
                      <Check className="h-3 w-3 text-white" strokeWidth={3}/>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-10 max-w-3xl text-[15px] md:text-[16px] text-brand-black">
            <span className="font-semibold">The real cost is not only what the listing materials cost.</span>{" "}
            <span className="text-brand-gray">It is what the process takes out of the agent's week.</span>
          </p>
        </div>
      </section>

      {/* ==================== NOT THE AGENT ==================== */}
      <section className="bg-brand-black text-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6">
              <div className="eyebrow text-white/60">07 · Operating Model</div>
              <h2 className="mt-4 text-[36px] md:text-6xl font-bold tracking-[-0.02em] leading-[1.02]">
                The agent should not be the <span className="text-brand-gradient">listing department.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 space-y-5 text-[16px] md:text-[17px] leading-relaxed text-white/70">
              <p>Agents create more value prospecting, advising, negotiating, following up, booking showings, and winning listings than they do trying to maintain listing standards between other tasks.</p>
              <p>When listing work gets squeezed between higher-priority client responsibilities, the standard usually drops. Photos get rushed. Details get missed. Website listings lag behind. Seller updates take longer. The listing feels less controlled than the agent intended.</p>
              <p className="text-white text-[19px] md:text-[22px] font-medium tracking-tight">
                This is not a talent problem. It is an operating-model problem.
              </p>
              <p>Done when there is time usually means lower quality, weaker consistency, and more follow-up later. LotWise Home gives the listing its own execution layer so the agent can stay focused on the work that actually moves the relationship, the transaction, and the business.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Intake selector */}
      <IntakeSelector />

      {/* ==================== PRICING ==================== */}
      <section id="pricing" className="bg-white text-brand-black">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-6">
              <div className="eyebrow-accent">08 · Pricing</div>
              <h2 className="mt-4 text-[34px] md:text-5xl font-bold tracking-tight leading-[1.05]">
                Choose the support level that fits your listing flow.
              </h2>
            </div>
            <p className="lg:col-span-6 text-[16px] leading-relaxed text-brand-gray max-w-xl">
              LotWise Home is built for agents, teams, and brokerages that want listings handled with
              a stronger standard. Start with one listing, build a repeatable monthly rhythm, or
              create a team-wide listing system.
            </p>
          </div>

          <div className="mt-12 border border-brand-gray/25 bg-[#F7F7F5] p-6 md:p-8">
            <div className="eyebrow-accent">How pricing works</div>
            <h3 className="mt-2 text-xl md:text-2xl font-bold tracking-tight">Pricing starts here. Final quotes are built around the listing.</h3>
            <p className="mt-3 text-[14.5px] leading-relaxed text-brand-gray max-w-3xl">
              Every listing is different. A downtown condo, a rural acreage, a family home, a luxury property, and a
              brokerage-wide listing system should not be priced the same way. LotWise Home provides starting points and
              typical ranges so agents can understand the investment level before reaching out. Final pricing is confirmed
              by quote after reviewing the property, location, timeline, and required deliverables. This keeps the service
              fair, flexible, and properly matched to the listing.
            </p>
          </div>

          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allPlans.map((p, i) => (
              <PricingCard key={p.id} plan={p} index={i} />
            ))}
          </div>

          <div className="mt-16">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="eyebrow">Add-ons</div>
                <h3 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">Add the support your listing needs.</h3>
              </div>
              <Link to="/pricing" className="hidden md:inline-flex items-center gap-2 text-[13px] font-bold link-underline">
                Full pricing details <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 border-t border-brand-gray/20 pt-4">
              {addOns.map((a) => (
                <div key={a.label} className="flex items-baseline justify-between border-b border-brand-gray/15 py-3">
                  <span className="text-[14px] text-brand-black">{a.label}</span>
                  <span className="text-[13px] text-brand-gray tabular-nums">{a.price}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[12.5px] text-brand-gray max-w-3xl">
              Available where applicable and quoted when needed. Subject to property type, location, and scheduling.
              Drone services are subject to weather, property location, flight conditions, and applicable rules. AI and
              video walkthroughs are quoted based on the required media, editing scope, and property complexity.
            </p>
          </div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section className="relative bg-[#F7F7F5] text-brand-black">
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none dot-grid-fade" aria-hidden />
        <div className="relative mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="eyebrow-accent">09 · Process</div>
            <h2 className="mt-4 text-[32px] md:text-5xl font-bold tracking-tight leading-[1.06]">
              A simple system for a better listing standard.
            </h2>
            <p className="mt-6 text-[16px] leading-relaxed text-brand-gray">
              LotWise Home works through the listing with a structured process: property review,
              photo capture, room and feature documentation, listing-ready assets, and support for
              presentation across MLS, agent websites, brokerage websites, and approved marketing
              channels. The goal is not to add complexity to the agent's workflow. It is to remove it.
            </p>
          </div>

          <Reveal className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {processSteps.map((s, i) => (
              <div key={s.t} className="bg-white p-6 border border-brand-gray/20">
                <div className="flex items-baseline justify-between">
                  <div className="text-[42px] font-bold tracking-tight text-brand-gradient num-tick leading-none">{String(i + 1).padStart(2, "0")}</div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-brand-gray">Step</div>
                </div>
                <h3 className="mt-4 text-[18px] font-bold tracking-tight">{s.t}</h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-brand-gray">{s.b}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ==================== MLS POSITIONING ==================== */}
      <section className="bg-white text-brand-black">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow-accent">10 · MLS + Owned Channels</div>
              <h2 className="mt-4 text-[32px] md:text-5xl font-bold tracking-tight leading-[1.06]">
                Built for MLS readiness and owned-channel presentation.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-4 text-[16px] leading-relaxed text-brand-gray">
              <p>MLS matters, but the listing does not stop there. Today's property presentation often has to support the MLS listing, Realtor.ca visibility through MLS where applicable, agent websites, brokerage websites, team pages, print materials, and social listing promotion.</p>
              <p>LotWise Home helps organize the listing foundation so the property can be presented more consistently wherever the agent needs it to appear.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {["MLS-ready materials", "Agent website listing support", "Brokerage website listing support", "Print + social selections"].map((x) => (
                  <div key={x} className="flex items-center gap-3 border border-brand-gray/20 p-4">
                    <span className="h-2 w-2 rounded-full bg-gradient-to-br from-brand-blue to-brand-violet" />
                    <span className="text-[14px] font-medium">{x}</span>
                  </div>
                ))}
              </div>
              <p className="text-[12.5px] italic text-brand-gray/90 pt-2">
                Note: LotWise Home does not claim direct MLS publishing access. Materials are prepared to MLS-ready standards. MLS access remains subject to agent, brokerage, board, and platform rules.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== WHY LOTWISE ==================== */}
      <section className="relative bg-brand-black text-white">
        <div className="absolute inset-0 dot-grid-dark opacity-25 pointer-events-none" aria-hidden />
        <div className="relative mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="eyebrow text-white/60">11 · Why LotWise Home</div>
            <h2 className="mt-4 text-[34px] md:text-5xl font-bold tracking-tight leading-[1.05]">
              Built for listings, not improvisation.
            </h2>
            <p className="mt-6 text-[16px] leading-relaxed text-white/60">
              LotWise Home is not built around whoever has time. It is built around a standard.
              The difference is not only output quality. It is ownership, consistency, and control.
              What feels easier to manage usually performs better.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {proofCards.map((c, i) => (
              <div key={c.t} className="border border-white/12 p-6 hover:border-brand-blue/60 transition-colors">
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">Proof {String(i + 1).padStart(2, "0")}</div>
                <div className="mt-4 text-[22px] font-bold tracking-tight">{c.t}</div>
                <p className="mt-3 text-[13.5px] text-white/60 leading-relaxed">{c.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SERVICE AREA ==================== */}
      <section id="service-area" className="bg-white text-brand-black">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow-accent">12 · Service Area</div>
              <h2 className="mt-4 text-[34px] md:text-5xl font-bold tracking-tight leading-[1.05]">
                Currently serving southeastern Ontario.
              </h2>
              <p className="mt-6 text-[15.5px] leading-relaxed text-brand-gray">
                Priority coverage through {regionalCoverage.slice(0, -1).join(", ")}, and {regionalCoverage.slice(-1)}. Agents,
                teams, and brokerages outside the active service area are welcome to apply for the waitlist.
              </p>
              <Link
                to="/contact#service-area"
                data-testid="service-area-cta"
                className="mt-8 inline-flex items-center gap-2 bg-brand-black text-white px-5 py-3.5 text-[13px] font-bold tracking-tight hover:bg-brand-violet transition-colors"
              >
                <MapPin className="h-4 w-4" /> Check Your Area
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {priorityCities.map((c, i) => (
                  <div key={c} className="border border-brand-gray/20 p-4 flex items-center justify-between hover:border-brand-black transition-colors">
                    <div>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-brand-gray">City {String(i + 1).padStart(2, "0")}</div>
                      <div className="mt-1 text-[15px] font-bold">{c}</div>
                    </div>
                    <span className="h-2 w-2 rounded-full bg-gradient-to-br from-brand-blue to-brand-violet" />
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {regionalCoverage.map((r) => (
                  <span key={r} className="text-[11px] tracking-[0.14em] uppercase text-brand-gray border border-brand-gray/25 px-2.5 py-1">{r}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section className="relative bg-[#F7F7F5] text-brand-black">
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none dot-grid-fade" aria-hidden />
        <div className="relative mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="eyebrow-accent">13 · FAQ</div>
            <h2 className="mt-4 text-[32px] md:text-5xl font-bold tracking-tight leading-[1.06]">
              Real answers, on the record.
            </h2>
          </div>

          <div className="mt-12 max-w-[900px]">
            <Accordion type="single" collapsible className="w-full" data-testid="home-faq-accordion">
              {faqItems.slice(0, 10).map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-brand-gray/25">
                  <AccordionTrigger className="text-left text-[16px] md:text-[17px] font-medium py-6 hover:no-underline">
                    <span className="pr-6">{f.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-brand-gray pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="mt-8">
              <Link to="/faq" className="inline-flex items-center gap-2 text-[13px] font-bold link-underline">
                See all questions <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="relative bg-brand-black text-white">
        <div className="absolute inset-0 dot-grid-dark opacity-30 pointer-events-none" aria-hidden />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden
          style={{
            background: "radial-gradient(60% 60% at 50% 50%, rgba(79,13,255,0.22) 0%, rgba(0,0,0,0) 65%)",
          }}
        />
        <div className="relative mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-40 text-center">
          <div className="eyebrow text-white/60">14 · Next Step</div>
          <h2 className="mt-6 text-[40px] md:text-7xl font-bold tracking-[-0.02em] leading-[1.0] max-w-[900px] mx-auto">
            Put your listings on a <span className="text-brand-gradient">system.</span>
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-[16px] md:text-[17px] leading-relaxed text-white/70">
            LotWise Home gives agents, teams, and brokerages a cleaner, more controlled way to
            prepare and present property listings. The standard should not depend on whoever has time.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              data-testid="final-cta-primary"
              className="group inline-flex items-center gap-2 bg-white text-brand-black px-7 py-4 text-[14px] font-bold tracking-tight hover:bg-brand-blue hover:text-white transition-colors"
            >
              Request Listing Support
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/pricing"
              data-testid="final-cta-secondary"
              className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-4 text-[14px] font-bold tracking-tight hover:border-white transition-colors"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
